import { useMemo, useState } from 'react';
import type { CreateSeriesResult, Service, Staff } from '@salonomy/types';
import { Button, Field, Modal, Pill, Select, TextArea, TextInput } from '@salonomy/ui';
import { useCreateSeries } from '../../data/recurring.js';
import { serviceSpanMin } from '../../data/services.js';
import { money } from '../../lib/format.js';
import { clockLabel, longDate, wallMinutesToUtcIso } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';
import { MAX_INTERVAL_WEEKS, MAX_OCCURRENCES, projectDates, seriesSummary } from './cadence.js';
import { SeriesResultPanel } from './SeriesResultPanel.js';

function parseTime(value: string): number | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!m) return null;
  return +m[1]! * 60 + +m[2]!;
}

const INTERVAL_PRESETS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Every week' },
  { value: 2, label: 'Every 2 weeks (fortnightly)' },
  { value: 3, label: 'Every 3 weeks' },
  { value: 4, label: 'Every 4 weeks' },
];

/**
 * WS7 phase 3 — the standing-appointment creator ("the standing Tuesday cut"). Set a staff member,
 * service, first date/time, cadence (every N weeks) and a count (M occurrences); the operator previews
 * the projected dates, then on submit the server tests every occurrence against the diary and we show
 * exactly which ones DON'T fit (never a silent drop — SeriesResultPanel).
 *
 * Mounted two ways (Integrate wires both):
 *  • From the New-appointment flow via MakeRecurringButton → opens this with `defaults` prefilled
 *    from the in-progress booking, so "make this a standing weekly" is one click off a new booking.
 *  • Standalone from the Settings "Recurring appointments" section.
 *
 * Sealed-book legibility: the API derives renterId from the chosen staff member's book; we mirror
 * that consequence inline (same wording as the StaffModal) so the operator sees a standing booking for
 * a sealed renter is itself sealed BEFORE they create it.
 */
export function RecurringSeriesModal({
  date,
  tz,
  chairs,
  services,
  onClose,
  defaults,
}: {
  /** The salon-zone date the first occurrence defaults to ("YYYY-MM-DD"). */
  date: string;
  tz: string;
  /** Staff a series can be booked for (the caller passes the RBAC-allowed list). */
  chairs: Staff[];
  services: Service[];
  onClose: () => void;
  defaults?: {
    staffId?: string;
    serviceId?: string;
    time?: string;
    clientDisplayName?: string;
    clientId?: string | null;
  };
}) {
  const create = useCreateSeries();
  const toast = useToast();
  const bookable = useMemo(() => services.filter((s) => s.active), [services]);

  const [staffId, setStaffId] = useState(defaults?.staffId ?? chairs[0]?.id ?? '');
  const [serviceId, setServiceId] = useState(defaults?.serviceId ?? bookable[0]?.id ?? '');
  const [clientName, setClientName] = useState(defaults?.clientDisplayName ?? '');
  const [firstDate, setFirstDate] = useState(date);
  const [time, setTime] = useState(defaults?.time ?? '10:00');
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [occurrenceCount, setOccurrenceCount] = useState(6);
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<CreateSeriesResult | null>(null);

  const member = chairs.find((s) => s.id === staffId);
  const service = bookable.find((s) => s.id === serviceId);
  const startMin = parseTime(time);

  // Sealed-book consequence. `renterId` is the server's resolved truth — non-null exactly when this
  // member owns a sealed book (it already folds in salon mode + any per-person override). A standing
  // booking for a sealed renter is itself sealed; the operator must see this BEFORE they create it,
  // exactly as the StaffModal surfaces it when a member is created/edited.
  const effectivelySealed = Boolean(member?.renterId);

  const previewDates = useMemo(
    () => projectDates({ firstDate, intervalWeeks, occurrenceCount }),
    [firstDate, intervalWeeks, occurrenceCount],
  );

  const countValid = occurrenceCount >= 1 && occurrenceCount <= MAX_OCCURRENCES;
  const intervalValid = intervalWeeks >= 1 && intervalWeeks <= MAX_INTERVAL_WEEKS;
  const valid =
    Boolean(staffId && service && startMin != null && firstDate) &&
    clientName.trim().length > 0 &&
    countValid &&
    intervalValid;

  async function submit() {
    if (!valid || !service || startMin == null) {
      toast.error('Add a client name, service, first date/time and a valid cadence.');
      return;
    }
    try {
      const res = await create.mutateAsync({
        staffId,
        serviceId,
        clientId: defaults?.clientId ?? null,
        clientDisplayName: clientName.trim(),
        firstStartAt: wallMinutesToUtcIso(firstDate, startMin, tz),
        intervalWeeks,
        occurrenceCount,
        notes: notes.trim() || null,
        depositPaid: 0,
      });
      setResult(res);
      if (res.conflicts.length > 0) {
        toast.error(
          `Series created — ${res.conflicts.length} date${res.conflicts.length === 1 ? '' : 's'} couldn’t fit. Review below.`,
        );
      } else {
        toast.success(`Standing booking created — ${res.created.length} dates booked.`);
      }
    } catch {
      toast.error('Could not create this series.');
    }
  }

  // Once we have a result, the modal becomes a read-only summary of what happened (the conflicts must
  // stay visible until the operator dismisses) — they don't get silently closed away.
  if (result) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Standing booking created"
        width={520}
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="muted" style={{ fontSize: 13 }}>
          {seriesSummary(intervalWeeks, occurrenceCount, service, member?.displayName ?? '')} for{' '}
          <strong>{clientName.trim() || 'this client'}</strong>.
        </div>
        <SeriesResultPanel created={result.created} conflicts={result.conflicts} tz={tz} />
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New standing booking"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || create.isPending}>
            {create.isPending ? 'Booking…' : 'Create series'}
          </Button>
        </>
      }
    >
      <Field label="Client">
        <TextInput
          autoFocus
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Client name"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="With">
          <Select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            {chairs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Service"
          hint={
            service ? `${money(service.priceFrom)} · ${serviceSpanMin(service)} min` : undefined
          }
        >
          <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            {bookable.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {effectivelySealed ? (
        <div
          className="muted"
          style={{
            fontSize: 12.5,
            background: 'var(--slm-porcelain)',
            border: '1px solid var(--slm-border)',
            borderRadius: 8,
            padding: '8px 10px',
            margin: '2px 0 6px',
          }}
        >
          {member?.displayName}’s clients are sealed — this standing booking is theirs alone,
          isolated from the rest of the salon.
        </div>
      ) : null}

      <div className="section-title" style={{ fontSize: 15, margin: '8px 0 2px' }}>
        Cadence
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="First date">
          <TextInput type="date" value={firstDate} onChange={(e) => setFirstDate(e.target.value)} />
        </Field>
        <Field label="Time">
          <TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <Field label="Repeats">
          <Select
            value={String(intervalWeeks)}
            onChange={(e) => setIntervalWeeks(Number(e.target.value))}
          >
            {INTERVAL_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Occurrences" hint={`1–${MAX_OCCURRENCES}`}>
          <TextInput
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_OCCURRENCES}
            value={String(occurrenceCount)}
            onChange={(e) => setOccurrenceCount(Number(e.target.value) || 0)}
          />
        </Field>
      </div>

      {/* Live preview of the standing slots — the operator sees the dates before committing. The
          server's conflict check is authoritative; this is a sanity preview only. */}
      {valid && startMin != null ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <Pill tone="cobalt">
              {seriesSummary(intervalWeeks, occurrenceCount, service, member?.displayName ?? '')}
            </Pill>
            <span className="muted mono" style={{ fontSize: 12 }}>
              at {clockLabel(startMin)}
            </span>
          </div>
          <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            {previewDates
              .slice(0, 4)
              .map((d) => longDate(d, tz))
              .join(' · ')}
            {previewDates.length > 4 ? ` · +${previewDates.length - 4} more` : ''}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
            On create, any of these the diary can’t fit (member off, salon shut, chair taken) are
            shown — never dropped silently.
          </div>
        </div>
      ) : null}

      <Field label="Notes">
        <TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional — copied to every occurrence"
        />
      </Field>
    </Modal>
  );
}
